import { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Info } from 'lucide-react';

export interface CATResultPayload {
  answers: number[];
  total: number;
  finishedAt?: string;
}

interface CATTestProps {
  initial?: number[];
  onCancel?: () => void;
  onNext: (result: CATResultPayload) => void;
}

export const CAT_QUESTIONS: { left: string; right: string }[] = [
  { left: 'Nunca tenho tosse', right: 'Tenho tosse o tempo todo' },
  { left: 'Não tenho nenhum catarro (secreção) no peito', right: 'O meu peito está cheio de catarro (secreção)' },
  { left: 'Não sinto nenhuma pressão no peito', right: 'Sinto uma grande pressão no peito' },
  { left: 'Não sinto falta de ar quando subo uma ladeira ou um andar de escada', right: 'Sinto bastante falta de ar quando subo uma ladeira ou um andar de escada' },
  { left: 'Não sinto nenhuma limitação nas minhas atividades em casa', right: 'Sinto-me muito limitado nas minhas atividades em casa' },
  { left: 'Sinto-me confiante para sair de casa, apesar da minha doença pulmonar', right: 'Não me sinto nada confiante para sair de casa, por causa da minha doença pulmonar' },
  { left: 'Durmo profundamente', right: 'Não durmo profundamente devido à minha doença pulmonar' },
  { left: 'Tenho muita energia (disposição)', right: 'Não tenho nenhuma energia (disposição)' },
];

export default function CATTest({ initial, onCancel, onNext }: CATTestProps) {
  const [answers, setAnswers] = useState<number[]>(initial ?? Array(8).fill(-1));
  const [step, setStep] = useState(0);

  const allAnswered = answers.every((answer) => answer >= 0);
  const total = answers.reduce((acc, cur) => (cur >= 0 ? acc + cur : acc), 0);

  const setAnswer = (index: number, value: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">COPD Assessment Test (CAT) — Português (Brasil)</p>
          <p className="text-sm opacity-90">Selecione 0 a 5 para cada item. Total final de 0 a 40.</p>
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <p className="mb-3 font-medium">Item {step + 1} de 8</p>
        <div className="mb-3 grid grid-cols-2 gap-4 text-sm">
          <div className="rounded-md border bg-gray-50 p-3">{CAT_QUESTIONS[step].left}</div>
          <div className="rounded-md border bg-gray-50 p-3 text-right">{CAT_QUESTIONS[step].right}</div>
        </div>

        <div className="flex items-center justify-between gap-2">
          {Array.from({ length: 6 }).map((_, option) => (
            <label
              key={option}
              className={`flex flex-1 cursor-pointer select-none items-center justify-center rounded-md border px-3 py-3 text-lg ${
                answers[step] === option ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                className="hidden"
                name={`cat_v2_q_${step}`}
                value={option}
                checked={answers[step] === option}
                onChange={() => setAnswer(step, option)}
              />
              {option}
            </label>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 hover:bg-gray-200"
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </button>

          <div className="text-sm text-gray-600">Selecionado: {answers[step] >= 0 ? answers[step] : '—'}</div>

          {step < 7 ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
              onClick={() => setStep((current) => Math.min(7, current + 1))}
              disabled={answers[step] < 0}
            >
              Próxima
              <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white hover:bg-green-700"
              onClick={() => allAnswered && onNext({ answers, total, finishedAt: new Date().toISOString() })}
              disabled={!allAnswered}
            >
              Concluir CAT
              <Check className="ml-1 h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <div className="text-sm text-gray-700">Pontuação total: {total}</div>
      </div>
    </div>
  );
}
